import { useMemo } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useGetProfile } from '@/query/queries/user/useGetProfile';
import { useGetVeriffStatus } from '@/query/queries/user/useGetVeriffStatus';
import { USER_STATUSES, VERIFF_CODES } from '@/helpers/constants';
import { getFullname } from '@/helpers/utils';
import { useUserStore } from '@/store/userStore';
import { USER_RESTRICTIONS } from '@/helpers/user';

export const useGetMe = () => {
  const { accessToken } = useUserStore(state => state);
  const { data: veriffStatus } = useGetVeriffStatus({ enabled: !!accessToken });
  const { data: profile, refetch: refetchProfile } = useGetProfile({
    enabled: !!accessToken,
  });

  const user = useMemo(() => {
    if (!accessToken) return null;
    const decodedData = jwtDecode(accessToken);
    return decodedData.user;
  }, [accessToken]);

  const isVerified =
    veriffStatus?.code === VERIFF_CODES.verified &&
    (profile?.status === USER_STATUSES.verified ||
      profile?.status === USER_STATUSES.pending_review);

  const { hasAddCashRestriction, hasInvestingRestriction, hasWithdrawRestriction } = useMemo(() => {
    return {
      hasAddCashRestriction: Boolean(
        profile?.restrictions.includes(USER_RESTRICTIONS.add_cash),
      ),
      hasWithdrawRestriction: Boolean(
        profile?.restrictions.includes(USER_RESTRICTIONS.withdraw),
      ),
      hasInvestingRestriction: Boolean(
        profile?.restrictions.includes(USER_RESTRICTIONS.investing),
      ),
    };
  }, [profile])

  return {
    accessToken,
    user: profile || user,
    profile,
    refetchProfile,
    fullName: getFullname(profile?.first_name, profile?.last_name),
    isSuspended: profile?.status === USER_STATUSES.suspended,
    isPendingReview: profile?.status === USER_STATUSES.pending_review,
    isVerified,
    isEmailVerified: profile?.is_email_verified,
    isPhoneVerified: profile?.is_phone_number_verified,
    canAddCash: Boolean(!hasAddCashRestriction),
    canWithdraw: Boolean(!hasWithdrawRestriction),
    canInvest: Boolean(profile && isVerified && !hasInvestingRestriction),
    hasAddCashRestriction,
    hasWithdrawRestriction,
    hasInvestingRestriction,
    veriffStatus,
  };
};
